import { FC, FocusEvent, ReactNode, useState } from 'react';
import { Button, Col, Flex, Row } from 'antd';
import { TbDotsVertical, TbStarOff } from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useResourceActionAccess } from '@/hooks/useResourceActionAccess';
import { useSetLifecycleFavorite } from '@/queries/tanstack/database/entities';
import {
    DashboardActionsDropdown,
    FolderActionsDropdown,
    InvestmentsActionsDropdown,
    LifecycleActionsDropdown,
    ListActionsDropdown,
    MemosActionsDropdown,
    OverflowFadeText,
    PermissionGuard,
    PipelinesActionsDropdown,
} from '@/shared';
import { getCollapsibleHoverRevealStyle } from '@/shared/components/layouts/hoverReveal';
import {
    getWorkspaceResourceLabel,
    WorkspaceResourceAvatar,
    WorkspaceResourceStatusIndicators,
} from '@/shared/components/workspace-resources';
import { getWorkspaceResourceDetailRoute } from '@/shared/workspaceResourceRoutes';
import {
    useDashboardStore,
    useFolderStore,
    useInvestmentStore,
    useListStore,
    useMemoStore,
    usePipelineStore,
} from '@/stores';

import { getFavoriteItemSelectionStyles } from './selectionStyles';

interface ResourceFavoriteItemProps {
    result: any;
    section: any;
}

const ResourceFavoriteItem: FC<ResourceFavoriteItemProps> = ({ result, section }): JSX.Element => {
    const [actionsOpen, setActionsOpen] = useState(false);
    const [focused, setFocused] = useState(false);
    const [hovering, setHovering] = useState(false);

    const { toggleFolderFavorite } = useFolderStore();
    const { toggleInvestmentFavorite } = useInvestmentStore();
    const { toggleMemoFavorite } = useMemoStore();
    const { togglePipelineFavorite } = usePipelineStore();
    // Relations
    const { toggleDashboardFavorite } = useDashboardStore();
    const { toggleListFavorite } = useListStore();
    const lifecycleFavorite = useSetLifecycleFavorite(section === 'lifecycles' ? result?.id : undefined);

    const navigate = useNavigate();

    const { token } = useAntDesignTheme();
    const permissionedSections = [
        'dashboards',
        'folders',
        'investments',
        'lifecycles',
        'lists',
        'memos',
        'pipelines',
        'teams',
    ];
    const favoriteAccess = useResourceActionAccess(section, 'update', result?.id);
    const favoriteGuardAccess = permissionedSections.includes(section) ? favoriteAccess : undefined;

    const actionDropdownButton = (
        <Button
            aria-expanded={actionsOpen}
            aria-label='Resource actions'
            className='left-sider-hover-action'
            icon={
                <TbDotsVertical
                    style={{
                        verticalAlign: '-0.125rem',
                        color: token.colorTextTertiary,
                    }}
                />
            }
            size='small'
            type='text'
        />
    );

    const sectionToActionDropdownMap = {
        folders: (
            <FolderActionsDropdown folder={result} onOpenChange={setActionsOpen} open={actionsOpen}>
                {actionDropdownButton}
            </FolderActionsDropdown>
        ),
        investments: (
            <InvestmentsActionsDropdown investment={result} onOpenChange={setActionsOpen} open={actionsOpen}>
                {actionDropdownButton}
            </InvestmentsActionsDropdown>
        ),
        lifecycles: (
            <LifecycleActionsDropdown lifecycle={result} onOpenChange={setActionsOpen} open={actionsOpen}>
                {actionDropdownButton}
            </LifecycleActionsDropdown>
        ),
        memos: (
            <MemosActionsDropdown memo={result} onOpenChange={setActionsOpen} open={actionsOpen}>
                {actionDropdownButton}
            </MemosActionsDropdown>
        ),
        pipelines: (
            <PipelinesActionsDropdown pipeline={result} onOpenChange={setActionsOpen} open={actionsOpen}>
                {actionDropdownButton}
            </PipelinesActionsDropdown>
        ),
        // Relations
        dashboards: (
            <DashboardActionsDropdown dashboard={result} onOpenChange={setActionsOpen} open={actionsOpen}>
                {actionDropdownButton}
            </DashboardActionsDropdown>
        ),
        lists: (
            <ListActionsDropdown list={result} onOpenChange={setActionsOpen} open={actionsOpen}>
                {actionDropdownButton}
            </ListActionsDropdown>
        ),
    } as Record<string, ReactNode>;

    const toggleRemoveFavoriteResourceMap = {
        folders: () => {
            toggleFolderFavorite(result?.id, !result?.favorited);
        },
        investments: () => {
            toggleInvestmentFavorite(result?.id, !result?.isFavorite);
        },
        lifecycles: () => {
            lifecycleFavorite.mutate(!result?.favorited);
        },
        memos: () => {
            toggleMemoFavorite(result?.id, !result?.favorited);
        },
        pipelines: () => {
            togglePipelineFavorite(result?.id, !result?.isFavorite);
        },
        // Relations
        dashboards: () => {
            toggleDashboardFavorite(result?.id, !result?.favorited);
        },
        lists: () => {
            toggleListFavorite(result?.id, !result?.favorited);
        },
    } as any;

    const resourceRoute = getWorkspaceResourceDetailRoute(section, result?.id);
    const active = location.pathname === resourceRoute || location.pathname.startsWith(`${resourceRoute}/`);
    const resourceLabel = getWorkspaceResourceLabel(result, 'Untitled Resource');
    const selectionStyles = getFavoriteItemSelectionStyles(active, token.colorBorder);
    const actionsVisible = hovering || focused || actionsOpen;
    const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
            setFocused(false);
        }
    };
    const handleResourceOpen = () => {
        setActionsOpen(false);
        setFocused(false);
        setHovering(false);
        navigate(resourceRoute);
    };

    return (
        <Row
            data-resource-favorite-item='true'
            key={result?.id}
            onBlur={handleBlur}
            onFocus={() => setFocused(true)}
            style={{
                ...selectionStyles.row,
                marginBottom: '0.25rem',
                width: '100%',
            }}
            onMouseEnter={() => {
                setHovering(true);
            }}
            onMouseLeave={() => {
                setHovering(false);
            }}
            wrap={false}
        >
            <Col flex='1 1 0' style={{ minWidth: 0 }}>
                <Button
                    key={result?.id}
                    style={{
                        textAlign: 'left',
                        paddingTop: 0,
                        paddingBottom: 0,
                        ...selectionStyles.button,
                        marginBottom: 0,
                        justifyContent: 'flex-start',
                        minWidth: 0,
                        overflow: 'hidden',
                    }}
                    size='small'
                    type='text'
                    block
                    onClick={handleResourceOpen}
                >
                    <Flex align='center' gap='0.375rem' style={{ minWidth: 0, width: '100%' }}>
                        <WorkspaceResourceAvatar
                            active={active}
                            listType={section === 'lists' ? result?.type : undefined}
                            resource={section === 'folders' ? 'folder' : section}
                            size={20}
                            style={{ flex: '0 0 auto' }}
                            website={result?.website}
                        />
                        <OverflowFadeText style={{ flex: '1 1 0%', lineHeight: 'inherit', width: 0 }}>
                            {resourceLabel}
                        </OverflowFadeText>
                        <WorkspaceResourceStatusIndicators
                            archived={result?.archived || result?.isArchived}
                            iconSize='0.875rem'
                            locked={result?.locked || result?.isLocked}
                            style={{ marginInlineStart: 0 }}
                        />
                    </Flex>
                </Button>
            </Col>
            <Col
                data-favorite-hover-action='remove'
                flex='none'
                style={getCollapsibleHoverRevealStyle(actionsVisible, token, {
                    expandedMaxWidth: '1.5rem',
                    gap: '0.25rem',
                })}
            >
                <PermissionGuard access={favoriteGuardAccess} tooltipPlacement='right'>
                    <Button
                        aria-label='Remove from Favorites'
                        className='left-sider-hover-action'
                        icon={
                            <TbStarOff
                                style={{
                                    verticalAlign: '-0.125rem',
                                    color: token.colorTextTertiary,
                                }}
                            />
                        }
                        size='small'
                        onClick={toggleRemoveFavoriteResourceMap[section]}
                        type='text'
                    />
                </PermissionGuard>
            </Col>
            <Col
                data-favorite-hover-action='overflow'
                flex='none'
                style={getCollapsibleHoverRevealStyle(actionsVisible, token, {
                    expandedMaxWidth: '1.5rem',
                    gap: '0.25rem',
                })}
            >
                {sectionToActionDropdownMap[section]}
            </Col>
        </Row>
    );
};

export default ResourceFavoriteItem;
